import React from 'react';
import { Navigate, NavLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDonate } from '../../contexts/PublicDonateContext';
import { formatCurrency } from '../portal/portalDonateShared';

export default function PublicDonateCartPage() {
  const navigate = useNavigate();
  const { cart, totalAmount, updateQuantity, removeItem, itemCount, donateMode } = usePublicDonate();
  const { isAuthenticated, loading: authLoading, staff } = useAuth();
  const hasPortalSession = !authLoading && isAuthenticated && staff?.portal;
  const [error, setError] = React.useState('');

  if (!donateMode && authLoading) {
    return (
      <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!donateMode && !hasPortalSession) {
    return <Navigate to="/donate" replace />;
  }

  const onCheckout = () => {
    if (!cart.length) {
      setError('Cart is empty');
      return;
    }
    navigate('/donate/checkout');
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Donate cart</Typography>
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {itemCount} item{itemCount === 1 ? '' : 's'} in cart
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" component={NavLink} to="/donate">
                Continue shopping
              </Button>
              <Button size="small" onClick={onCheckout}>
                Checkout
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {cart.length === 0 ? (
        <Alert severity="info">Your cart is empty. Add products from Donate.</Alert>
      ) : (
        <>
          {cart.map((item) => {
            const key = `${item.product_id}-${item.variation_id || 0}`;
            const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
            const lineTotal = Number(item.unit_price || 0) * qty;
            return (
              <Card key={key} variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {item.product_name || `Product #${item.product_id}`}
                      </Typography>
                      {item.variation_name ? (
                        <Typography variant="body2" color="text.secondary">
                          {item.variation_name}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        Unit: {formatCurrency(item.unit_price)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        label="Qty"
                        size="small"
                        type="number"
                        value={qty}
                        inputProps={{ min: 1, step: 1 }}
                        onChange={(e) => updateQuantity(item.product_id, item.variation_id || 0, e.target.value)}
                        sx={{ width: 90 }}
                      />
                      <Typography sx={{ minWidth: 100, textAlign: 'right', fontWeight: 700 }}>
                        {formatCurrency(lineTotal)}
                      </Typography>
                      <IconButton
                        color="error"
                        onClick={() => removeItem(item.product_id, item.variation_id || 0)}
                        aria-label="Remove item"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}

          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{formatCurrency(totalAmount)}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
